const COLORS = {
  accent: '#00D1FF',
  success: '#00FFA3',
  error: '#FF3B30',
  warn: '#F0B429',
  purple: '#A78BFA',
  sub: '#8B949E',
};

const UNPAID_EXPIRY_HOURS = 72;

export const PAYOUT_TIMING_COPY = 'Once funds are released, payouts are processed as quickly as possible. Bank settlement may take up to 2 business days depending on payment runs, weekends, and bank processing.';
export const PAYOUT_TIMING_SHORT = 'Payout processing · up to 2 business days';
export const PAYMENT_PROVIDER_DISCLOSURE = 'Your payment is securely held by our third-party payment provider until the transaction conditions are met.';
export const PAYMENT_PROTECTED_BODY = 'Your payment has been confirmed and is protected. Follow the next steps shown on your TrustTrade transaction.';

export const CUSTOMER_PRESENTATION = {
  AGREEMENT: 'AGREEMENT',
  AWAITING_PAYMENT: 'AWAITING_PAYMENT',
  PAYMENT_PROTECTED: 'PAYMENT_PROTECTED',
  DELIVERY_IN_PROGRESS: 'DELIVERY_IN_PROGRESS',
  AWAITING_BUYER_CONFIRMATION: 'AWAITING_BUYER_CONFIRMATION',
  DISPUTED: 'DISPUTED',
  FUNDS_RELEASED: 'FUNDS_RELEASED',
  PAYOUT_PROCESSING: 'PAYOUT_PROCESSING',
  PAID: 'PAID',
  REFUNDED: 'REFUNDED',
  CANCELLED: 'CANCELLED',
  EXPIRED: 'EXPIRED',
};

export const ESCROW_FLOW_STEPS = [
  { key: 'CREATED', label: 'Agreement' },
  { key: 'FUNDED', label: 'Awaiting payment' },
  { key: 'ESCROW_LOCKED', label: 'Payment Protected' },
  { key: 'DELIVERY_PENDING', label: 'Delivery / Handover' },
  { key: 'DELIVERED', label: 'Awaiting buyer confirmation' },
  { key: 'RELEASED', label: 'Funds Released' },
];

function customerRole(options = {}) {
  if (!options || typeof options !== 'object' || options instanceof Date) return '';
  return String(options.role || '').trim().toLowerCase();
}

function isSellerRole(options = {}) {
  const role = customerRole(options);
  return role === 'seller' || role === 'freelancer';
}

export function fieldText(...values) {
  return values.filter(Boolean).join(' ').toLowerCase();
}

export function canonicalFinancialState(value) {
  return String(value || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
}

const AWAITING_PAYMENT_STATES = new Set([
  'AWAITING_PAYMENT', 'READY_FOR_PAYMENT', 'PENDING_PAYMENT',
  'AWAITING_PAYMENT_INTO_ESCROW', 'PAYMENT_PENDING',
]);
const CANCELLED_STATES = new Set(['CANCELLED', 'CANCELED']);
const REFUNDED_STATES = new Set(['REFUNDED']);

export function hasAuthoritativeReleaseEvidence(transaction = {}) {
  if (canonicalFinancialState(transaction.tradesafe_state) === 'FUNDS_RELEASED') return true;
  const evidence = transaction.provider_release_evidence || {};
  return canonicalFinancialState(evidence.status) === 'PROVEN' &&
    Array.isArray(evidence.basis) && evidence.basis.length > 0 &&
    !evidence.amount_mismatch && !evidence.payout_amount_mismatch && !evidence.expected_amount_missing;
}

export function hasTrustTradeDirectAcceptRelease(transaction = {}) {
  const method = String(transaction.release_confirmation_method || '').toLowerCase();
  if (method !== 'accept_delivery') return false;
  const state = canonicalFinancialState(transaction.tradesafe_state);
  if (!state || state === 'PENDING_ACCEPTANCE') return false;
  return Boolean(transaction.delivery_confirmed || transaction.buyer_confirmed_receipt_at);
}

export function hasAuthoritativeSettlementEvidence(transaction = {}) {
  return canonicalFinancialState(transaction.bank_payout_status) === 'PAID';
}

// Display-only: bank settlement is claimed only when evidence AND its recorded time exist.
export function confirmedSettlementAt(transaction = {}) {
  if (!hasAuthoritativeSettlementEvidence(transaction)) return null;
  return transaction.settlement_confirmed_at || transaction.bank_payout_evidence_at || null;
}

const SMART_DEAL_ALIAS_STATES = new Set(['COMPLETE', 'COMPLETED', 'RELEASED', 'APPROVED']);

export function resolveSmartDealFinancialPresentation(record = {}, options = {}) {
  const status = canonicalFinancialState(record.status);
  if (!SMART_DEAL_ALIAS_STATES.has(status)) return null;
  const released = hasAuthoritativeReleaseEvidence(record);
  const settled = Boolean(confirmedSettlementAt(record));
  if (released && settled) {
    return { label: isSellerRole(options) ? 'Payout Complete' : 'Paid', tone: 'settled', state: 'COMPLETED' };
  }
  if (released) {
    return {
      label: isSellerRole(options) ? 'Payout Processing' : 'Funds Released',
      tone: 'released',
      state: 'RELEASED',
    };
  }
  return { label: 'Payout Processing', tone: 'pending', state: 'PENDING_PAYOUT' };
}

export function resolveSmartDealProgress(record = {}, options = {}) {
  const status = canonicalFinancialState(record.status);
  const financial = resolveSmartDealFinancialPresentation(record, options);
  const steps = ['PENDING', 'ACCEPTED', 'FUNDED', 'DELIVERED', 'COMPLETE'];
  let current = status === 'DISPUTED' ? 'DELIVERED' : status === 'PAYMENT_PENDING' ? 'ACCEPTED' : status;
  let finalLabel = 'Confirm';
  let finalComplete = false;
  if (['COMPLETE', 'COMPLETED', 'RELEASED', 'APPROVED'].includes(status)) {
    if (financial?.state === 'COMPLETED') {
      current = 'COMPLETE';
      finalLabel = financial.label;
      finalComplete = true;
    } else if (financial?.state === 'RELEASED') {
      current = 'COMPLETE';
      finalLabel = financial.label;
      finalComplete = false;
    } else {
      current = 'DELIVERED';
      finalLabel = 'Payout Processing';
      finalComplete = false;
    }
  }
  return { steps, current, finalLabel, finalComplete, financial };
}

export function parseTransactionDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatExpiryCountdown(msRemaining) {
  const hours = Math.max(0, msRemaining) / (1000 * 60 * 60);
  if (hours < 1) return 'less than 1 hour';
  if (hours < 24) return `${Math.ceil(hours)} hour${Math.ceil(hours) === 1 ? '' : 's'}`;
  const days = hours / 24;
  return `${Math.ceil(days)} day${Math.ceil(days) === 1 ? '' : 's'}`;
}

export function getPendingPaymentExpiry(transaction = {}, now = new Date()) {
  const referenceDate = parseTransactionDate(
    transaction.awaiting_payment_at ||
    transaction.payment_requested_at ||
    transaction.payment_due_at
  );

  if (!referenceDate) {
    return {
      isAwaitingPayment: false,
      isExpired: false,
      expiresAt: null,
      expiresInLabel: null,
      ageHours: null,
    };
  }

  const states = [transaction.payment_status, transaction.transaction_status, transaction.transaction_state, transaction.tradesafe_state]
    .map(canonicalFinancialState);
  const isPaymentPhase = states.some(state => AWAITING_PAYMENT_STATES.has(state));
  const terminal = [transaction.payment_status, transaction.transaction_state, transaction.payout_status, transaction.tradesafe_state]
    .map(canonicalFinancialState);
  if (!isPaymentPhase || terminal.some(state =>
    state === 'EXPIRED' || CANCELLED_STATES.has(state) || REFUNDED_STATES.has(state) ||
    state === 'FUNDS_RELEASED' || state === 'PAID_OUT'
  )) {
    return {
      isAwaitingPayment: false,
      isExpired: false,
      expiresAt: null,
      expiresInLabel: null,
      ageHours: null,
    };
  }

  const expiryAt = new Date(referenceDate.getTime() + UNPAID_EXPIRY_HOURS * 60 * 60 * 1000);
  const msRemaining = expiryAt.getTime() - now.getTime();
  const ageHours = (now.getTime() - referenceDate.getTime()) / (1000 * 60 * 60);

  return {
    isAwaitingPayment: true,
    isExpired: msRemaining <= 0,
    expiresAt: expiryAt.toISOString(),
    expiresInLabel: msRemaining <= 0 ? 'Transaction expired due to no payment' : `Expires in ${formatExpiryCountdown(msRemaining)}`,
    ageHours,
  };
}

export function isCourierAutoReleaseEnabled() {
  const raw = String(process.env.REACT_APP_COURIER_AUTO_RELEASE_ENABLED || '').trim().toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
}

export function getCourierBuyerConfirmCopy() {
  return "Please confirm once you've received and checked the item.";
}

export function getTransactionFlowType(transaction = {}) {
  const deliveryMethod = fieldText(transaction.delivery_method);
  if (
    deliveryMethod.includes('bank_deposit') ||
    deliveryMethod.includes('in-person') ||
    deliveryMethod.includes('in person') ||
    deliveryMethod.includes('handover') ||
    deliveryMethod.includes('meeting')
  ) {
    return 'instant';
  }
  const method = fieldText(
    transaction.delivery_method,
    transaction.transaction_type,
    transaction.release_type,
    transaction.fulfillment_type
  );

  if (method.includes('courier') || method.includes('delivery') || method.includes('shipping') || method.includes('physical')) {
    return 'delivery';
  }
  if (
    method.includes('digital') ||
    method.includes('instant') ||
    method.includes('immediate') ||
    method.includes('bank_deposit') ||
    method.includes('in-person') ||
    method.includes('in person') ||
    method.includes('handover') ||
    method.includes('meeting')
  ) {
    return 'instant';
  }
  return 'neutral';
}

export function isInPersonTransaction(transaction = {}) {
  const method = fieldText(
    transaction.delivery_method,
    transaction.transaction_type,
    transaction.release_type,
    transaction.fulfillment_type
  );
  return method.includes('bank_deposit') || method.includes('in-person') || method.includes('in person') || method.includes('handover') || method.includes('meeting');
}

// Every provider state (transaction- or allocation-level, as stored in
// tradesafe_state) reached only after the buyer's deposit was confirmed.
// Mirrors backend transaction_state.PROVIDER_FUNDED_STATES.
export const PROVIDER_FUNDED_STATES = [
  'FUNDS_DEPOSITED', 'FUNDS_RECEIVED',
  'INITIATED', 'SENT', 'IN_TRANSIT', 'PENDING_ACCEPTANCE',
  'RENEGOTIATION', 'DISPUTED', 'DISPUTED_EXTENDED', 'DISPUTED_RENEGOTIATE',
  'DELIVERED', 'DELIVERY_ACCEPTED',
  'FUNDS_RELEASED',
];

export function isProviderFundingConfirmed(transaction = {}) {
  // `transaction = {}` only guards an omitted/undefined argument — a caller
  // whose transaction state starts as `useState(null)` (e.g.
  // PaymentCheckout, before its data has loaded) passes an explicit null
  // through untouched, crashing the whole page on first render. Found live
  // navigating to /pay in SIT testing (2026-10-06).
  const t = transaction || {};
  const tradesafeState = String(t.tradesafe_state || '').toUpperCase();
  if (t.tradesafe_id) return PROVIDER_FUNDED_STATES.includes(tradesafeState);

  // Legacy non-TradeSafe records have no provider state to inspect.
  const payment = canonicalFinancialState(t.payment_status);
  return new Set(['PAID', 'FUNDS_SECURED']).has(payment);
}

export function getPaymentSuccessCopy(transaction = {}) {
  return {
    title: 'Payment Protected',
    body: PAYMENT_PROTECTED_BODY,
    disclosure: PAYMENT_PROVIDER_DISCLOSURE,
    flowType: getTransactionFlowType(transaction),
  };
}

export const PROVIDER_CHECKOUT_ORIGINS = [
  'https://pay-sandbox.tradesafe.dev',
  'https://pay.tradesafe.co.za',
  'https://pay.tradesafe.dev',
];

export function isTrustedCheckoutMessage(event = {}, options = {}) {
  const origin = String(event.origin || '').replace(/\/$/, '');
  if (!PROVIDER_CHECKOUT_ORIGINS.includes(origin)) return false;
  const data = event.data || {};
  if (data.type !== 'tradesafePaymentResponse') return false;
  if (options.expectedSource && event.source !== options.expectedSource) return false;
  const payloadRef = data.transactionId || data.transaction_id || data.reference || data.id
    || data.dealId || data.deal_id;
  if (!payloadRef) return true;
  const refs = [
    options.transactionId,
    options.dealId,
    options.milestoneId,
    options.dealId && options.milestoneId ? `${options.dealId}-${options.milestoneId}` : null,
  ].filter(Boolean).map(String);
  if (!refs.length) return true;
  return refs.includes(String(payloadRef));
}

export function getPaymentResultCopy({ uiEvent, transaction } = {}) {
  if (isProviderFundingConfirmed(transaction)) {
    return {
      title: 'Payment Protected',
      body: PAYMENT_PROTECTED_BODY,
      disclosure: PAYMENT_PROVIDER_DISCLOSURE,
      canRetry: false,
      phase: 'protected',
    };
  }
  const status = String(uiEvent?.status || '').toLowerCase();
  if (status === 'success') {
    return {
      title: 'Payment submitted',
      body: 'Your payment has been submitted. TrustTrade is waiting for the payment provider to confirm it. This page will update to Payment Protected once confirmation is received.',
      disclosure: PAYMENT_PROVIDER_DISCLOSURE,
      canRetry: false,
      phase: 'submitted',
    };
  }
  if (status === 'canceled' || status === 'cancelled') {
    return {
      title: 'Payment not completed',
      body: 'Payment was not completed. No Payment Protected confirmation has been recorded yet.',
      disclosure: PAYMENT_PROVIDER_DISCLOSURE,
      canRetry: true,
      phase: 'cancelled',
    };
  }
  if (status === 'error' || status === 'failure' || status === 'failed') {
    return {
      title: 'Payment unsuccessful',
      body: 'Payment was unsuccessful. You can try again if this attempt did not complete.',
      disclosure: PAYMENT_PROVIDER_DISCLOSURE,
      canRetry: true,
      phase: 'failed',
    };
  }
  return {
    title: 'Payment status being verified',
    body: 'Payment status is being verified with our payment provider. TrustTrade will update this transaction when confirmation is available.',
    disclosure: PAYMENT_PROVIDER_DISCLOSURE,
    canRetry: false,
    phase: 'unknown',
  };
}

export function getFlowCopy(transaction = {}) {
  const flowType = getTransactionFlowType(transaction);
  if (flowType === 'delivery') {
    return {
      flowType,
      securedLabel: 'Payment Protected',
      securedBuyer: 'Your payment is protected. Follow the next steps shown on this TrustTrade transaction.',
      securedSeller: 'Payment is protected. Dispatch the item and update the transaction.',
      progressLabel: 'Delivery in progress',
      progressDescription: 'Delivery is in progress. Buyer confirmation is required before funds are released.',
      confirmationLabel: 'Awaiting buyer confirmation',
      confirmationDescription: 'Delivery has been marked complete. Buyer confirmation is required before funds are released.',
      confirmAction: 'Confirm receipt',
      sellerAction: 'Mark as dispatched',
    };
  }
  if (flowType === 'instant') {
    if (isInPersonTransaction(transaction)) {
      return {
        flowType,
        securedLabel: 'Payment Protected',
        securedBuyer: 'Your payment is protected. Follow the next steps shown on this TrustTrade transaction.',
        securedSeller: 'Payment is protected. Arrange the in-person handover; no dispatch action is required.',
        progressLabel: 'Handover in progress',
        progressDescription: 'Complete the in-person handover, then the buyer confirms to release funds.',
        confirmationLabel: 'Handover confirmed',
        confirmationDescription: 'Handover confirmed. Waiting for funds to be released.',
        confirmAction: 'Confirm handover',
        sellerAction: 'View transaction',
      };
    }
    return {
      flowType,
      securedLabel: 'Payment Protected',
      securedBuyer: 'Your payment is protected. Follow the next steps shown on this TrustTrade transaction.',
      securedSeller: 'Payment is protected. No delivery dispatch action is required for this digital flow.',
      progressLabel: 'Handover in progress',
      progressDescription: 'Complete the agreed digital handover. Buyer confirmation is required before funds are released.',
      confirmationLabel: 'Completion confirmed',
      confirmationDescription: 'Completion confirmed. Waiting for funds to be released.',
      confirmAction: 'View release status',
      sellerAction: 'View transaction',
    };
  }
  return {
    flowType,
    securedLabel: 'Payment Protected',
    securedBuyer: 'Your payment is protected. Follow the next steps shown on this TrustTrade transaction.',
    securedSeller: 'Payment is protected. Complete the agreed conditions and update the transaction.',
    progressLabel: 'Delivery / Handover',
    progressDescription: 'The agreed handover conditions are being completed.',
    confirmationLabel: 'Awaiting buyer confirmation',
    confirmationDescription: 'Completion confirmed. Waiting for funds to be released.',
    confirmAction: 'Confirm completion',
    sellerAction: 'Update completion',
  };
}

export function getTransactionActionAvailability(transaction = {}, options = {}) {
  const { isBuyer = false, isSeller = false, isActionable = true } = options;
  const flowType = getTransactionFlowType(transaction);
  const isInstantFlow = flowType === 'instant';
  const allowsDeliveryActions = !isInstantFlow;
  const hasEscrow = Boolean(transaction.tradesafe_id);
  const escrowState = String(transaction.tradesafe_state || '').toUpperCase();
  const paymentStatus = transaction.payment_status || '';
  const notConfirmed = !transaction.delivery_confirmed;

  return {
    canStartDelivery: isActionable && allowsDeliveryActions && hasEscrow && isSeller && escrowState === 'FUNDS_DEPOSITED' && notConfirmed,
    paymentStillSettling: isActionable && allowsDeliveryActions && hasEscrow && isSeller && escrowState === 'FUNDS_RECEIVED' && notConfirmed,
    deliveryMarkedStarted: allowsDeliveryActions && hasEscrow && isSeller && ['INITIATED', 'SENT', 'DELIVERED'].includes(escrowState) && notConfirmed,
    canManualStartDelivery: isActionable && allowsDeliveryActions && hasEscrow && isSeller && ['Paid', 'Funds Secured'].includes(paymentStatus) && ['FUNDS_RECEIVED', 'FUNDS_DEPOSITED'].includes(escrowState) && !['INITIATED', 'DELIVERED'].includes(escrowState),
    canAcceptDeliveryTS: isActionable && allowsDeliveryActions && hasEscrow && isBuyer && ['INITIATED', 'SENT', 'DELIVERED'].includes(escrowState) && notConfirmed,
    canManualAcceptDelivery: isActionable && allowsDeliveryActions && hasEscrow && isBuyer && notConfirmed && (paymentStatus === 'Delivery in Progress' || transaction.delivery_started_at || escrowState === 'INITIATED'),
    canConfirmInstantRelease: isActionable && isInstantFlow && hasEscrow && isBuyer && escrowState === 'FUNDS_RECEIVED' && notConfirmed,
    buyerWaitingForSellerDispatch: allowsDeliveryActions && hasEscrow && isBuyer && ['FUNDS_RECEIVED', 'FUNDS_DEPOSITED'].includes(escrowState) && notConfirmed,
  };
}

export function getPaymentDisplayAmounts(transaction = {}, estimates = {}) {
  const estimatedTotal = Number(estimates.buyerPays || 0);
  const estimatedProcessingCost = Number(estimates.processingCost || 0);
  const providerTotal = Number(transaction.provider_total_paid);
  const providerBundledFee = Number(transaction.provider_processing_fee);
  const trustTradeFee = Number(transaction.trusttrade_fee ?? transaction.platform_fee ?? estimates.trustTradeFee ?? 0);
  const hasProviderActuals = isProviderFundingConfirmed(transaction) && Number.isFinite(providerTotal) && providerTotal > 0 && Number.isFinite(providerBundledFee) && providerBundledFee >= 0;

  return {
    hasProviderActuals,
    buyerPays: hasProviderActuals ? providerTotal : estimatedTotal,
    bundledServiceFee: hasProviderActuals ? providerBundledFee : null,
    processingCost: hasProviderActuals ? Math.max(0, Math.round((providerBundledFee - trustTradeFee) * 100) / 100) : estimatedProcessingCost,
  };
}

// Mirrors getPaymentDisplayAmounts' estimate-vs-provider-actual pattern for
// the seller side: seller_payout_provider_confirmed is only ever set by the
// backend (services/provider_release_evidence.py) from TradeSafe's own
// allocation.calculation.payout, once funds are proven released — never
// computed here. Before that, the pre-payment estimate the caller passes in
// is shown, clearly marked isEstimate=true so it's never confused with the
// authoritative figure (found live, 2026-10-06: a R500 CARD transaction's
// estimate of R477.50 differed from the real provider payout of R469.87).
export function getSellerPayoutDisplayAmount(transaction = {}, estimatedSellerReceives = 0) {
  const authoritativeAmount = Number(transaction.net_amount ?? transaction.seller_receives);
  const hasAuthoritativePayout = Boolean(transaction.seller_payout_provider_confirmed) && Number.isFinite(authoritativeAmount);
  return {
    isEstimate: !hasAuthoritativePayout,
    amount: hasAuthoritativePayout ? authoritativeAmount : Number(estimatedSellerReceives || 0),
  };
}

export function getTransactionLimitCopy(minimumAmount, maximumAmount) {
  const minimum = Number(minimumAmount || 0);
  const maximum = Number(maximumAmount || 0);
  const minimumCopy = `Minimum transaction amount is R${minimum.toLocaleString('en-ZA')}.`;
  return maximum > 0
    ? `${minimumCopy} Maximum transaction amount is R${maximum.toLocaleString('en-ZA')}.`
    : `${minimumCopy} No maximum is currently configured.`;
}

// The ONE shared definition of "is this dispute status terminal" for the
// whole frontend — mirrors backend/services/dispute_state.py EXACTLY:
// TERMINAL_DISPUTE_STATUSES = {"Resolved"}. The backend only ever writes
// Pending/Appealed/Resolved and treats anything that isn't explicitly
// Resolved — Pending, Appealed, any other/future status, or empty/unknown —
// as blocking (its own stated fail-safe default for a fund-release gate).
// Every frontend dispute-open check (this file's hasOpenDispute, and
// pages/dashboardMoneyState.js's isDisputeOpen) must call this rather than
// keep its own word-list: a second definition is exactly how these two
// drifted apart before (this file previously also closed on CLOSED/
// DISMISSED/CANCELLED/CANCELED, none of which the backend has ever written
// or treats as terminal).
export function isDisputeStatusOpen(status) {
  return canonicalFinancialState(status) !== 'RESOLVED';
}

export function hasOpenDispute(transaction, disputes = []) {
  // has_dispute is the authoritative, backend-maintained flag (kept in sync by
  // services/dispute_state.py::sync_has_dispute on every dispute status change)
  // and is always present on the transaction document — check it first. The
  // embedded transaction.dispute/transaction.disputes fields below are legacy
  // shapes some callers may still populate; GET /api/transactions/{id} itself
  // never embeds dispute records, so a caller that only has the plain
  // transaction object (no separate /api/disputes fetch) still correctly
  // detects an open dispute via this flag instead of silently treating it as
  // actionable until a release attempt hits a 409.
  if (transaction?.has_dispute === true) return true;
  const localStatuses = [transaction?.dispute_status, transaction?.dispute?.status];
  if (localStatuses.some(status => status && isDisputeStatusOpen(status))) return true;
  return disputes.some((dispute) =>
    Boolean(dispute.transaction_id) &&
    dispute.transaction_id === transaction?.transaction_id &&
    isDisputeStatusOpen(dispute.status)
  );
}

// Bank payout stage is written only from provider statement evidence
// (backend services/provider_release_evidence.py); "Paid" requires ACSP/paid.
export function getPayoutStageCopy(transaction = {}, options = {}) {
  const settlement = 'Bank settlement may take up to 2 business days.';
  const bankStage = String(transaction.bank_payout_status || '').trim().toLowerCase();
  const payoutStatus = canonicalFinancialState(transaction.payout_status);
  const seller = isSellerRole(options);
  const releasedBuyer = {
    label: 'Funds Released',
    description: 'The payment has been released to the seller.',
  };
  const releasedSeller = {
    label: 'Payout Processing',
    description: 'Your funds have been released and your bank payout is being processed.',
  };
  const releasedCopy = seller ? releasedSeller : releasedBuyer;
  switch (bankStage) {
    case 'paid':
      return {
        stage: 'paid',
        label: seller ? 'Payout Complete' : 'Paid',
        description: seller ? 'Your bank payout is complete.' : 'The seller has been paid.',
      };
    case 'processing':
    case 'pdng':
      return { stage: 'processing', ...releasedCopy, description: `${releasedCopy.description} ${settlement}` };
    case 'initiated':
      return { stage: 'initiated', ...releasedCopy, description: `${releasedCopy.description} ${settlement}` };
    case 'failed':
      return { stage: 'failed', label: 'Bank payout needs attention', description: 'Funds have been released, but the bank payout needs attention. TrustTrade support is reviewing it.' };
    case 'unknown':
      return { stage: 'unknown', label: 'Bank payout reconciliation required', description: 'Funds have been released, but the latest bank payout status is not yet verified. TrustTrade support must reconcile it before it can be treated as paid.' };
    default:
      if (bankStage) {
        return { stage: 'unknown', label: 'Bank payout reconciliation required', description: 'Funds have been released, but the latest bank payout status is not recognized. TrustTrade support must reconcile it before it can be treated as paid.' };
      }
      if (new Set([
        'PAYOUT_FAILED', 'PAYOUT_REJECTED', 'WITHDRAWAL_FAILED',
        'WITHDRAWAL_REJECTED', 'BANK_PAYOUT_REJECTED',
      ]).has(payoutStatus)) {
        return { stage: 'failed', label: 'Bank payout needs attention', description: 'Funds have been released, but the bank payout needs attention. TrustTrade support is reviewing it.' };
      }
      if (new Set(['PAYOUT_RECONCILIATION_REQUIRED', 'RECONCILIATION_REQUIRED', 'UNKNOWN_OUTCOME', 'IDENTITY_REVIEW_REQUIRED']).has(payoutStatus)) {
        return { stage: 'unknown', label: 'Bank payout reconciliation required', description: 'Funds have been released, but the latest bank payout status is not yet verified. TrustTrade support must reconcile it before it can be treated as paid.' };
      }
      return { stage: 'released', ...releasedCopy, description: `${releasedCopy.description} ${PAYOUT_TIMING_COPY}` };
  }
}

export function getConfirmedAwaitingReleaseCopy(transaction = {}) {
  const flowType = getTransactionFlowType(transaction);
  const label = isInPersonTransaction(transaction)
    ? 'Handover confirmed'
    : flowType === 'delivery' ? 'Receipt confirmed' : 'Completion confirmed';
  const description = transaction.tradesafe_buyer_confirmation_pending
    ? 'Waiting for funds to be released. You may also receive a separate confirmation request from our payment provider.'
    : 'Waiting for funds to be released.';
  return { label, description };
}

export function resolveEscrowUiState(transaction = {}, disputes = [], options = {}) {
  const flow = getFlowCopy(transaction);
  const payment = canonicalFinancialState(transaction.payment_status);
  const transactionStates = [transaction.transaction_status, transaction.transaction_state].map(canonicalFinancialState);
  const delivery = canonicalFinancialState(transaction.delivery_status);
  const tradesafe = canonicalFinancialState(transaction.tradesafe_state);

  const buyerConfirmed = Boolean(transaction.buyer_confirmed);
  const sellerConfirmed = Boolean(transaction.seller_confirmed);
  const bothConfirmed = buyerConfirmed && sellerConfirmed;
  const deliveryConfirmed = Boolean(transaction.delivery_confirmed) ||
    ['DELIVERED', 'DELIVERY_CONFIRMED', 'CONFIRMED'].includes(delivery) ||
    ['DELIVERED', 'DELIVERY_ACCEPTED'].includes(tradesafe);
  const deliveryStarted = Boolean(transaction.delivery_started_at) ||
    ['IN_PROGRESS', 'IN_TRANSIT', 'SENT', 'DELIVERED'].includes(delivery) ||
    ['INITIATED', 'SENT', 'IN_TRANSIT', 'DELIVERED', 'DELIVERY_ACCEPTED'].includes(tradesafe) ||
    ['DELIVERY_IN_PROGRESS', 'DISPATCHED'].includes(payment);
  const fundsSecured = isProviderFundingConfirmed(transaction);
  const released = hasAuthoritativeReleaseEvidence(transaction) || hasTrustTradeDirectAcceptRelease(transaction);
  const buyerConfirmedRelease = Boolean(transaction.delivery_confirmed) || Boolean(transaction.buyer_confirmed_receipt_at);
  const completed = hasAuthoritativeSettlementEvidence(transaction);
  const payoutStage = getPayoutStageCopy(transaction, options);

  const exactStates = [
    transaction.payment_status, transaction.release_status, transaction.transaction_status,
    transaction.transaction_state, transaction.payout_status, transaction.tradesafe_state,
  ].map(canonicalFinancialState);
  const isRefunded = exactStates.some(state => REFUNDED_STATES.has(state));
  const isCancelled = exactStates.some(state => CANCELLED_STATES.has(state));
  if (isCancelled || isRefunded) {
    return {
      state: isRefunded ? 'REFUNDED' : 'CANCELLED',
      presentationState: isRefunded ? CUSTOMER_PRESENTATION.REFUNDED : CUSTOMER_PRESENTATION.CANCELLED,
      label: isRefunded ? 'Refunded' : 'Cancelled',
      description: isRefunded ? 'This payment has been refunded.' : 'This transaction is no longer active.',
      color: COLORS.error,
      bg: 'rgba(255,59,48,0.1)',
      progressIndex: 0,
      terminal: true,
      actionable: false,
    };
  }

  if (released && completed && payoutStage.stage === 'paid') {
    return {
      state: 'COMPLETED',
      presentationState: CUSTOMER_PRESENTATION.PAID,
      label: payoutStage.label,
      description: payoutStage.description,
      secondaryLabel: 'Paid',
      color: COLORS.success,
      bg: 'rgba(0,255,163,0.1)',
      progressIndex: ESCROW_FLOW_STEPS.length,
      terminal: true,
      actionable: false,
    };
  }

  if (released && ['failed', 'unknown'].includes(payoutStage.stage)) {
    return {
      state: 'PAYOUT_REVIEW',
      presentationState: CUSTOMER_PRESENTATION.PAYOUT_PROCESSING,
      label: payoutStage.label,
      description: payoutStage.description,
      secondaryLabel: 'Support review required',
      color: COLORS.error,
      bg: 'rgba(255,59,48,0.1)',
      progressIndex: ESCROW_FLOW_STEPS.length - 1,
      terminal: false,
      actionable: false,
    };
  }

  if (released && ['initiated', 'processing'].includes(payoutStage.stage)) {
    return {
      state: 'RELEASED',
      presentationState: isSellerRole(options) ? CUSTOMER_PRESENTATION.PAYOUT_PROCESSING : CUSTOMER_PRESENTATION.FUNDS_RELEASED,
      label: payoutStage.label,
      description: payoutStage.description,
      secondaryLabel: PAYOUT_TIMING_SHORT,
      color: COLORS.success,
      bg: 'rgba(0,255,163,0.1)',
      progressIndex: ESCROW_FLOW_STEPS.length - 1,
      terminal: false,
      actionable: false,
    };
  }

  // Preserve completed rendering only for legacy, non-provider records. A
  // provider transaction needs release and bank-settlement evidence above.
  if (completed && !transaction.tradesafe_id) {
    return {
      state: 'COMPLETED',
      presentationState: CUSTOMER_PRESENTATION.PAID,
      label: 'Paid',
      description: 'Payment is complete.',
      color: COLORS.success,
      bg: 'rgba(0,255,163,0.1)',
      progressIndex: ESCROW_FLOW_STEPS.length,
      terminal: true,
      actionable: false,
    };
  }

  if (released) {
    return {
      state: 'RELEASED',
      presentationState: isSellerRole(options) ? CUSTOMER_PRESENTATION.PAYOUT_PROCESSING : CUSTOMER_PRESENTATION.FUNDS_RELEASED,
      label: payoutStage.label,
      description: payoutStage.description,
      secondaryLabel: PAYOUT_TIMING_SHORT,
      color: COLORS.success,
      bg: 'rgba(0,255,163,0.1)',
      progressIndex: ESCROW_FLOW_STEPS.length - 1,
      terminal: false,
      actionable: false,
    };
  }

  if (hasOpenDispute(transaction, disputes)) {
    return {
      state: 'DISPUTED',
      presentationState: CUSTOMER_PRESENTATION.DISPUTED,
      label: 'Under Review',
      description: 'A dispute is open. TrustTrade is reviewing the case. Funds stay protected until a resolution.',
      color: COLORS.error,
      bg: 'rgba(255,59,48,0.1)',
      progressIndex: 4,
      terminal: false,
      actionable: false,
    };
  }

  const explicitExpiry = getPendingPaymentExpiry(transaction);
  if (
    transaction.archived ||
    payment === 'EXPIRED' ||
    transactionStates.includes('EXPIRED') ||
    tradesafe === 'EXPIRED' ||
    explicitExpiry.isExpired
  ) {
    return {
      state: 'EXPIRED',
      label: 'Transaction expired',
      description: 'Transaction expired due to no payment.',
      secondaryLabel: 'No payment received within 72 hours',
      color: '#6E7681',
      bg: 'rgba(100,116,139,0.1)',
      progressIndex: 2,
      terminal: true,
      actionable: false,
      expiresAt: explicitExpiry.expiresAt,
    };
  }

  if (!bothConfirmed) {
    return {
      state: 'CREATED',
      presentationState: CUSTOMER_PRESENTATION.AGREEMENT,
      label: 'Awaiting agreement',
      description: 'Both parties must confirm the transaction terms.',
      color: COLORS.warn,
      bg: 'rgba(240,180,41,0.1)',
      progressIndex: 1,
      terminal: false,
      actionable: true,
    };
  }

  const paymentExpiry = getPendingPaymentExpiry(transaction);
  const awaitingPayment = !fundsSecured && (
    AWAITING_PAYMENT_STATES.has(payment) ||
    transactionStates.some(state => AWAITING_PAYMENT_STATES.has(state)) ||
    ['CREATED', 'PENDING'].includes(tradesafe)
  );

  if (awaitingPayment) {
    if (paymentExpiry.isExpired) {
      return {
        state: 'EXPIRED',
        label: 'Transaction expired',
        description: 'Transaction expired due to no payment.',
        secondaryLabel: 'No payment received within 72 hours',
        color: '#6E7681',
        bg: 'rgba(100,116,139,0.1)',
        progressIndex: 2,
        terminal: true,
        actionable: false,
        expiresAt: paymentExpiry.expiresAt,
      };
    }
    return {
      state: 'FUNDED',
      presentationState: CUSTOMER_PRESENTATION.AWAITING_PAYMENT,
      label: 'Awaiting payment',
      description: 'Waiting for the buyer to complete payment.',
      secondaryLabel: paymentExpiry.expiresInLabel,
      expiresAt: paymentExpiry.expiresAt,
      color: COLORS.accent,
      bg: 'rgba(0,209,255,0.1)',
      progressIndex: 2,
      terminal: false,
      actionable: true,
    };
  }

  if (buyerConfirmedRelease) {
    const confirmed = getConfirmedAwaitingReleaseCopy(transaction);
    return {
      state: 'DELIVERED',
      presentationState: CUSTOMER_PRESENTATION.AWAITING_BUYER_CONFIRMATION,
      label: confirmed.label,
      description: confirmed.description,
      color: COLORS.success,
      bg: 'rgba(0,255,163,0.1)',
      progressIndex: 5,
      terminal: false,
      actionable: false,
    };
  }

  if (fundsSecured && !deliveryStarted) {
    return {
      state: 'ESCROW_LOCKED',
      presentationState: CUSTOMER_PRESENTATION.PAYMENT_PROTECTED,
      label: flow.securedLabel,
      description: flow.securedBuyer,
      color: COLORS.success,
      bg: 'rgba(0,255,163,0.1)',
      progressIndex: 3,
      terminal: false,
      actionable: true,
    };
  }

  if (deliveryStarted && !deliveryConfirmed) {
    return {
      state: 'DELIVERY_PENDING',
      presentationState: CUSTOMER_PRESENTATION.DELIVERY_IN_PROGRESS,
      label: flow.progressLabel,
      description: flow.progressDescription,
      color: COLORS.purple,
      bg: 'rgba(167,139,250,0.12)',
      progressIndex: 4,
      terminal: false,
      actionable: true,
    };
  }

  return {
    state: 'DELIVERED',
    presentationState: CUSTOMER_PRESENTATION.AWAITING_BUYER_CONFIRMATION,
    label: flow.confirmationLabel,
    description: flow.confirmationDescription,
    color: COLORS.success,
    bg: 'rgba(0,255,163,0.1)',
    progressIndex: 5,
    terminal: false,
    actionable: true,
  };
}

export function mapEscrowUiStateToTimelineState(uiState) {
  switch (uiState?.state) {
    case 'COMPLETED':
    case 'RELEASED':
      return 'COMPLETED';
    case 'EXPIRED':
      return 'EXPIRED';
    case 'DISPUTED':
      return 'DELIVERY_IN_PROGRESS';
    case 'DELIVERED':
      return 'DELIVERED';
    case 'DELIVERY_PENDING':
      return 'DELIVERY_IN_PROGRESS';
    case 'ESCROW_LOCKED':
      return 'PAYMENT_SECURED';
    case 'FUNDED':
      return 'AWAITING_PAYMENT';
    case 'CREATED':
    default:
      return 'PENDING_CONFIRMATION';
  }
}
